<template>
  <div class="card-box">
    <b-row class="mt-1 mb-5">
      <b-col cols="10">
        <div class="excluded-included-size">
          <span class="font-weight-bold"> {{ productsFoundLabel }}</span>
          {{ labelValue }}
          <custom-tooltip :tooltipText="tooltipValue" :section="isIncludeCard ? 'exclude-card' : 'include-card'">
          </custom-tooltip>
        </div>
      </b-col>
      <b-col cols="2">
        <div class="d-flex flex-row-reverse">
          <div class="ml-3" v-if="isIncludeCard">
            <b-button
              size="sm"
              @click="onExcludeClick"
              :disabled="selectedRows.length === 0"
              data-test="onProductIdsChangeBtn"
              variant="primary"
              >{{ $t('scoringProfiles.insight.exclude') }}
              <font-awesome-icon :icon="['far', 'arrow-down']" />
            </b-button>
          </div>
          <div v-else>
            <b-button
              size="sm"
              variant="primary"
              @click="onIncludeClick"
              :disabled="selectedRows.length === 0"
              data-test="onProductIdsChangeBtn"
              >{{ $t('scoringProfiles.insight.include') }}
              <font-awesome-icon :icon="['far', 'arrow-up']" />
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <div>
          <b-table
            class="ffk-custom-scroll"
            :fields="tableFields"
            :items="tableItems"
            small
            hover
            striped
            selectable
            no-select-on-click
            no-sort-reset
            sticky-header
            no-border-collapse
            ref="scoringCoverageTable"
            :tbody-tr-class="rowClass"
          >
            <template v-slot:head()="data">
              <b-form-checkbox
                v-if="data.column === 'productPerimetersHandler'"
                v-model="isAllProductIdsSelected"
                @change="selectUnselectAllRows()"
              ></b-form-checkbox>
              {{ $t(data.label) }}
            </template>

            <template v-slot:cell(productPerimetersHandler)="{ item }">
              <b-form-checkbox :value="item.productId" v-model="selectedRows" @change="selectUnselectRow()">
              </b-form-checkbox>
            </template>

            <template v-slot:cell(isUnderTesting)="{ item }">
              <span v-if="item.isUnderTesting" class="px-2">
                <pit-svg :color="item.isExcludedFromPerimeter ? '#6c757d' : '#000'"></pit-svg>
              </span>
              <span v-else class="px-2 pointer" @click.stop.prevent="() => changeFollow(item)">
                <followed-icon :is-followed="item.isFollowed"></followed-icon>
              </span>
            </template>

            <template v-slot:cell(issuerName)="cell">
              <b-link size="sm" @click="onClickTableRow(cell.item)" class="link-to text-decoration-none text-dark">
                <span>{{ cell.item.issuerName }}</span>
              </b-link>
            </template>

            <template v-slot:cell(name)="cell">
              <b-link size="sm" @click="onClickTableRow(cell.item)" class="link-to text-decoration-none text-dark">
                <span>{{ cell.item.name }}</span>
              </b-link>
            </template>

            <template v-slot:cell(scoringProfile)="{ item }">
              <span>{{ item.scoringProfile }}</span>
            </template>
          </b-table>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import PitSvg from '../Lib/PitSvg'
import FollowedIcon from '../Lib/FollowedIcon'
import SimpleBox from '../Lib/SimpleBox'
import CustomTooltip from '../Lib/CustomTooltip.vue'

const SCORING_PROFILE_FIELDS = [
  {
    key: 'productPerimetersHandler',
    label: '',
    class: 'checkbox',
    sortable: false,
  },
  {
    key: 'isUnderTesting',
    label: '',
    class: 'link',
  },
  {
    key: 'issuerName',
    label: 'scoringProfiles.insight.issuer',
    class: 'issuer',
    sortable: true,
  },
  {
    key: 'name',
    label: 'scoringProfiles.insight.name',
    class: 'name',
    sortable: true,
  },
  {
    key: 'scoringProfile',
    label: 'scoringProfiles.insight.scoringProfile',
    class: 'scoringProfile',
    sortable: true,
  },
]

export default {
  components: { PitSvg, FollowedIcon, SimpleBox, CustomTooltip },
  props: {
    tableItems: Array,
    productsFoundLabel: String,
    isIncludeCard: {
      type: Boolean,
      default: false,
    },
    labelValue: String,
    tooltipValue: String,
  },
  data() {
    return {
      selectedRows: [],
      isAllProductIdsSelected: false,
      tableFields: SCORING_PROFILE_FIELDS,
    }
  },
  methods: {
    rowClass(item, type) {
      if (!item || type !== 'row') return
      return item.isExcludedFromPerimeter ? 'text-muted' : 'text-dark'
    },
    onExcludeClick() {
      this.$emit('on-exclude-click', this.selectedRows)
      this.selectedRows = []
      this.isAllProductIdsSelected = false
    },
    onIncludeClick() {
      this.$emit('on-include-click', this.selectedRows)
      this.selectedRows = []
      this.isAllProductIdsSelected = false
    },
    selectUnselectAllRows() {
      if (this.isAllProductIdsSelected) {
        this.selectedRows = this.tableItems.map((el) => el.productId).slice(0)
      } else {
        this.selectedRows = []
      }
    },
    selectUnselectRow() {
      if (this.selectedRows.length === this.tableItems.length) {
        this.isAllProductIdsSelected = true
      } else {
        this.isAllProductIdsSelected = false
      }
    },
    onClickTableRow(value) {
      this.$emit('on-click-table-row', value)
    },
  },
}
</script>

<style lang="scss">
.card-box {
  padding: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--gray8);
}

.table.b-table > thead > tr > .table-b-table-default,
.table.b-table > tbody > tr > .table-b-table-default,
.table.b-table > tfoot > tr > .table-b-table-default {
  background-color: var(--primaryBGColor);

  &.scoringProfile {
    width: 20%;
  }
  &.checkbox,
  &.link {
    width: 3%;
  }
}

.b-table-sticky-header > .table.b-table > thead > tr > th {
  position: sticky !important;
}

.excluded-included-size {
  font-size: 0.875rem;
}
</style>
